import { Dropdown } from "semantic-ui-react";
import { Accessor, Filter, PropertyAttribute, PropertyFilter, Range, RangeBound, Scalar } from "../../../types/types";
import Modal, { ModalProps } from "../../Modal/Modal";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useDataTableContext } from "../../../contexts/DataTableContext";
import { NON_QUERYABLE_ACCESSORS, NON_QUERYABLE_ATTRIBUTES } from "../../../hooks/useTableColumns";
import { FilterType } from "../../../hooks/usePropertyFilters";
import Preloader from "../Preloader/Preloader";
import Input from "../Input/Input";
import DatePicker from "../DatePicker/DatePicker";
import Button from "../Button/Button";
import clsx from "clsx";
import { toIsoDate } from "../../../dates";
import "./FilterModal.scss";

// ISO dates will sort as strings.
const isGreater = (a: RangeBound, b: RangeBound) =>
	typeof a === "number" && typeof b === "number" ? a > b : String(a) > String(b);

interface FilterModalProps extends ModalProps {
	filter?: Filter | PropertyFilter;
	onSaveFilter: (filter: Filter | PropertyFilter) => void;
}

export default function FilterModal({ filter, onSaveFilter, ...forward }: FilterModalProps) {
	const {
		accessors: allAccessors,
		propertyAttributes,
		aggregates,
		getFriendlyColumnName,
		getFilterTypeFromAggregates,
	} = useDataTableContext()!;
	const [selectedColumn, setSelectedColumn] = useState<Accessor | PropertyAttribute | undefined>(
		filter && "accessor" in filter ? filter.accessor : undefined
	);

	const [term, setTerm] = useState<Scalar | Range | undefined>(filter?.term);

	const accessors: Accessor[] = useMemo(() => {
		if (!aggregates) return [];

		return allAccessors.filter((accessor) => {
			if (accessor in aggregates.values)
				// NB: Filter out distinct value accessors with no actual values
				return aggregates.values[accessor]?.length > 0;
			else if (accessor in aggregates.mins)
				// NB: Filter out range value accessors with no actual range
				return aggregates.mins[accessor] !== aggregates.maxs[accessor];

			return true;
		});
	}, [allAccessors, aggregates]);

	const clearTerm = () => setTerm(undefined);

	useEffect(() => {
		if (forward.open === false) setSelectedColumn(undefined);
	}, [forward.open]);

	const verb = filter ? "Edit" : "Add";

	const options = useMemo(() => {
		const propertyOptions = propertyAttributes
			.filter((attribute) => !NON_QUERYABLE_ATTRIBUTES.includes(attribute))
			.map((attribute) => ({
				key: attribute,
				text: getFriendlyColumnName(attribute),
				value: attribute,
			}));

		const accessorOptions = accessors
			.filter((accessor) => !NON_QUERYABLE_ACCESSORS.includes(accessor))
			.map((accessor) => ({
				key: accessor,
				text: getFriendlyColumnName(accessor),
				value: accessor,
			}));
		return [...propertyOptions, ...accessorOptions].sort((a, b) => ("" + a.text).localeCompare(b.text));
	}, [accessors, propertyAttributes, getFriendlyColumnName]);

	const onChangeColumn = (column: Accessor | PropertyAttribute) => {
		setSelectedColumn(column);
		clearTerm();
	};

	const onChangeRange = (lower: boolean, value: RangeBound | null) => {
		let updated: Array<RangeBound | null> = [null, null];

		if (Array.isArray(term)) updated = [...term];

		const index = lower ? 0 : 1;

		updated[index] = value;

		if (updated[0] === null && updated[1] === null) clearTerm();
		else setTerm(updated as Range);
	};

	const isPropertyAttributeSelected = (column: Accessor | PropertyAttribute): column is PropertyAttribute => {
		return propertyAttributes.includes(column as PropertyAttribute);
	};

	const filterType =
		aggregates && selectedColumn && !isPropertyAttributeSelected(selectedColumn)
			? getFilterTypeFromAggregates(selectedColumn)
			: FilterType.PATTERN;

	const onConfirm = () => {
		if (!selectedColumn || term === undefined) {
			return;
		}

		const submitted =
			filterType === FilterType.DATE_RANGE && Array.isArray(term)
				? (term.map((bound) => toIsoDate(bound) ?? bound) as Range)
				: term;

		if (isPropertyAttributeSelected(selectedColumn)) {
			onSaveFilter({
				attribute: selectedColumn,
				term: submitted,
			});
		} else {
			onSaveFilter({
				accessor: selectedColumn,
				term: submitted,
			});
		}
	};

	const isRangeFilter = filterType === FilterType.RANGE || filterType === FilterType.DATE_RANGE;
	// Check that the bounds are valid in range filters.
	const [lowerBound, upperBound]: Array<RangeBound | null> =
		isRangeFilter && Array.isArray(term) ? term : [null, null];
	const isInvertedRange = lowerBound !== null && upperBound !== null && isGreater(lowerBound, upperBound);

	let controls: ReactNode;

	const patternControls = (
		<>
			contains
			<Input onChange={(e, { value }) => setTerm(value)} />
		</>
	);

	if (!aggregates) {
		controls = <Preloader variant="inline" />;
	} else if (!selectedColumn) {
		controls = <span></span>;
	} else if (isPropertyAttributeSelected(selectedColumn)) {
		controls = patternControls;
	} else {
		switch (filterType) {
			case FilterType.RANGE:
			case FilterType.DATE_RANGE: {
				const min = aggregates.mins[selectedColumn];
				const max = aggregates.maxs[selectedColumn];

				// Keep anything unparseable (which would otherwise serialise to null) out of the filter.
				const parseNumber = (value: string): number | null => {
					if (!value.length) return null;

					const parsed = Number(value);

					return Number.isFinite(parsed) ? parsed : null;
				};

				const renderBound = (lower: boolean) => {
					const bound = lower ? lowerBound : upperBound;

					// Bound each input by the other end of the range, so the picker itself can't invert it.
					const lowerLimit = lower ? min : (lowerBound ?? min);
					const upperLimit = lower ? (upperBound ?? max) : max;

					if (filterType === FilterType.DATE_RANGE)
						return (
							<DatePicker
								value={bound}
								min={lowerLimit}
								max={upperLimit}
								error={isInvertedRange}
								onChange={(date) => onChangeRange(lower, date)}
							/>
						);

					return (
						<Input
							type="number"
							placeholder={(lower ? min : max).toString()}
							min={lowerLimit}
							max={upperLimit}
							error={isInvertedRange}
							value={bound ?? ""}
							onChange={(e, { value }) => onChangeRange(lower, parseNumber(value))}
						/>
					);
				};

				controls = (
					<>
						between
						{renderBound(true)}
						and
						{renderBound(false)}
					</>
				);
				break;
			}

			case FilterType.VALUES: {
				// NB: Don't allow empty strings as options
				const valueOptions = aggregates.values[selectedColumn]
					.filter((value) => value && value.toString().length > 0)
					.map((value) => ({
						key: value!,
						text: value!,
						value: value!,
					}));

				controls = (
					<>
						is
						<Dropdown
							placeholder="Select value"
							fluid
							search
							selection
							multiple
							options={valueOptions}
							// @ts-ignore
							defaultValue={filter?.term}
							onChange={(e, { value }) => setTerm(value as Scalar)}
						/>
					</>
				);
				break;
			}

			default:
				controls = patternControls;
				break;
		}
	}

	// An array term (a range, or a set of selected values) needs at least one entry to filter on
	const isSubmittable =
		!!selectedColumn && (Array.isArray(term) ? term.some((value) => value !== null) : !!term) && !isInvertedRange;

	return (
		<Modal title={`${verb} filter`} className="filter-modal" {...forward}>
			<div
				className={clsx({
					"filter-modal-content": true,
					"no-accessor": !selectedColumn,
				})}
			>
				<Dropdown
					placeholder="Select field"
					fluid
					search
					selection
					options={options}
					defaultValue={filter && "accessor" in filter ? filter.accessor : undefined}
					onChange={(e, { value }) => onChangeColumn(value as Accessor | PropertyAttribute)}
				/>
				{controls}
			</div>
			{isInvertedRange && (
				<p className="range-error">
					{filterType === FilterType.DATE_RANGE
						? "The start date must not be after the end date."
						: "The first value must not be greater than the second."}
				</p>
			)}
			<Button right variant="primary" className="confirm" disabled={!isSubmittable} onClick={onConfirm}>
				OK
			</Button>
		</Modal>
	);
}
